import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeftRight, ShieldCheck, Check, Undo2, EyeOff, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { EmptyLegMarket } from "@/components/EmptyLegMarket";

export default function Bourse() {
  const [available, setAvailable] = useState([]);
  const [mine, setMine] = useState([]);

  const load = () => {
    api.get("/bourse").then((r) => setAvailable(r.data));
    api.get("/bourse/mine").then((r) => setMine(r.data));
  };
  useEffect(() => { load(); }, []);

  const accept = async (rid) => {
    try { await api.post(`/bourse/${rid}/accept`); toast.success("Course acceptée — ajoutée à votre planning (zéro commission)"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const withdraw = async (rid) => {
    try { await api.post(`/bourse/${rid}/withdraw`); toast.success("Course retirée de la bourse"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <ArrowLeftRight className="h-6 w-6 text-primary" /> Bourse d'échange
        </h1>
        <p className="text-muted-foreground text-sm flex items-center gap-1">
          <ShieldCheck className="h-4 w-4 text-green-600" /> Données patient anonymisées (RGPD) · zéro commission, zéro frais.
        </p>
      </div>

      <Tabs defaultValue="available">
        <TabsList>
          <TabsTrigger value="available" data-testid="bourse-tab-available">Disponibles ({available.length})</TabsTrigger>
          <TabsTrigger value="mine" data-testid="bourse-tab-mine">Mes courses poussées ({mine.length})</TabsTrigger>
          <TabsTrigger value="empty-legs" data-testid="bourse-tab-empty-legs"><Sparkles className="h-3.5 w-3.5 mr-1" /> Trajets à vide (IA)</TabsTrigger>
        </TabsList>

        <TabsContent value="available" className="space-y-3 mt-4" data-testid="bourse-available">
          {available.length === 0 && <Card className="p-8 rounded-md shadow-none text-center text-muted-foreground">Aucune course disponible dans vos tribus.</Card>}
          {available.map((r) => (
            <Card key={r.id} className="p-4 rounded-md shadow-none flex flex-wrap items-center justify-between gap-3" data-testid={`bourse-ride-${r.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{new Date(r.date).toLocaleDateString("fr-FR")} · {r.time}</span>
                  <Badge variant="outline" className="capitalize">{r.type}</Badge>
                  <Badge variant="outline" className="gap-1 text-[10px]"><EyeOff className="h-3 w-3" /> {r.patient_name}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{r.pickup_city} → {r.dropoff_city} · {r.distance_km} km</p>
                <p className="text-xs text-muted-foreground">Proposé par {r.original_company_name} · {r.tribu_name}</p>
              </div>
              <Button className="gap-2" onClick={() => accept(r.id)} data-testid={`bourse-accept-${r.id}`}><Check className="h-4 w-4" /> Accepter</Button>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="mine" className="space-y-3 mt-4" data-testid="bourse-mine">
          {mine.length === 0 && <Card className="p-8 rounded-md shadow-none text-center text-muted-foreground">Aucune course poussée. Depuis Jeregul, poussez vos surcharges sur la bourse.</Card>}
          {mine.map((r) => (
            <Card key={r.id} className="p-4 rounded-md shadow-none flex flex-wrap items-center justify-between gap-3" data-testid={`bourse-mine-${r.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{r.time} · {r.patient_name}</span>
                  <Badge variant="outline" className="capitalize">{r.type}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{r.pickup_address} → {r.dropoff_address} · {r.distance_km} km</p>
                <Badge variant="outline" className="border-accent bg-accent/20 text-amber-700 dark:text-amber-300 mt-1">En attente d'un confrère</Badge>
              </div>
              <Button variant="outline" className="gap-2" onClick={() => withdraw(r.id)} data-testid={`bourse-withdraw-${r.id}`}><Undo2 className="h-4 w-4" /> Retirer</Button>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="empty-legs" className="mt-4" data-testid="bourse-empty-legs">
          <EmptyLegMarket canPublish={true} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
